import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Eye, EyeOff, ShieldCheck, Loader2, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { useToast } from '../hooks/use-toast';
import { auth, saveSession } from '../api';
import { brand } from '../mock';

export default function Login() {
  const nav = useNavigate();
  const { toast } = useToast();
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', remember: true });
  const [lockInfo, setLockInfo] = useState(null); // { message, contactEmail }

  // If the axios interceptor bounced the user here after a 403 lock, surface the message
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('ad_lock_info');
      if (raw) {
        setLockInfo(JSON.parse(raw));
        sessionStorage.removeItem('ad_lock_info');
      }
    } catch { /* ignore */ }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setLockInfo(null);
    try {
      const res = await auth.login({ email: form.email, password: form.password });
      saveSession(res);
      const u = res.user;
      // Locked firm — owners can pay to unlock; teammates must wait for the owner
      if (u.accessStatus === 'locked') {
        if (u.firmRole === 'owner' || u.isPlatformAdmin) {
          toast({ title: 'Account locked', description: 'Pick a plan to restore access.' });
          setTimeout(() => nav('/pay'), 400);
        } else {
          setLockInfo({
            message: u.lockedReason || 'Your firm is locked. Ask the firm owner to renew.',
            contactEmail: 'jpstevens13@gmail.com',
          });
        }
        return;
      }
      toast({ title: 'Welcome back', description: 'Redirecting to your dashboard...' });
      setTimeout(() => nav('/dashboard'), 400);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.code === 'firm_locked') {
        setLockInfo({
          message: detail.message || 'Your account access has been suspended.',
          contactEmail: detail.contactEmail || 'jpstevens13@gmail.com',
        });
      } else {
        const msg = typeof detail === 'string' ? detail : 'Sign in failed. Please try again.';
        toast({ title: 'Sign in failed', description: msg, variant: 'destructive' });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#fdfaf3] grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-gradient-to-br from-emerald-900 via-sky-900 to-emerald-950 text-white relative overflow-hidden">
        <div className="pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full bg-sky-500/20 blur-3xl z-0"/>
        <div className="pointer-events-none absolute bottom-0 left-0 h-64 w-64 rounded-full bg-emerald-500/20 blur-3xl z-0"/>
        <Link to="/" className="flex items-center gap-2">
          <div className="h-10 w-10 rounded-xl bg-black overflow-hidden grid place-items-center"><img src="/brand/accu-desk-logo.png" alt="Accu Desk" className="h-full w-full object-contain"/></div>
          <div>
            <div className="font-semibold">{brand.name}</div>
            <div className="text-[10px] uppercase tracking-wider text-emerald-200">{brand.tagline}</div>
          </div>
        </Link>
        <div className="max-w-md">
          <h2 className="text-4xl font-serif font-semibold leading-tight">Every deadline. Every client. One log-in.</h2>
          <p className="mt-4 text-emerald-100 leading-relaxed">Pick up exactly where you left off &mdash; tasks, timesheets, audits and invoices, all in one place.</p>
          <div className="mt-8 flex items-center gap-3 text-sm text-emerald-100">
            <ShieldCheck className="h-5 w-5 text-emerald-300"/> POPIA-aligned. Encrypted at rest and in transit.
          </div>
        </div>
        <div className="text-xs text-emerald-200/80">&copy; {new Date().getFullYear()} Saber Accounting (Pty) Ltd</div>
      </div>

      <div className="flex items-center justify-center p-8 lg:p-12">
        <form onSubmit={submit} className="w-full max-w-md">
          <div className="lg:hidden mb-8 flex items-center gap-2">
            <div className="h-10 w-10 rounded-xl bg-black overflow-hidden grid place-items-center"><img src="/brand/accu-desk-logo.png" alt="Accu Desk" className="h-full w-full object-contain"/></div>
            <div className="font-semibold text-stone-900">{brand.name}</div>
          </div>
          <h1 className="text-3xl font-serif font-semibold text-stone-900">Sign in to Accu Desk</h1>
          <p className="mt-2 text-stone-600">Welcome back. Enter your details to continue.</p>

          <div className="mt-8 space-y-4">
            <div>
              <Label htmlFor="email">Work email</Label>
              <Input id="email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@firm.co.za" className="mt-1.5 h-11"/>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <button type="button" className="text-xs text-emerald-800 hover:text-emerald-900">Forgot password?</button>
              </div>
              <div className="relative mt-1.5">
                <Input id="password" required type={show ? 'text' : 'password'} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Enter password" className="h-11 pr-10"/>
                <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-500 hover:text-stone-800">
                  {show ? <EyeOff size={16}/> : <Eye size={16}/>}
                </button>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="remember" checked={form.remember} onCheckedChange={(v) => setForm({ ...form, remember: !!v })}/>
              <Label htmlFor="remember" className="text-sm font-normal text-stone-700">Keep me signed in on this device</Label>
            </div>
          </div>

          <Button type="submit" disabled={busy} className="mt-8 w-full h-11 bg-emerald-800 hover:bg-emerald-900 text-white">
            {busy ? <><Loader2 size={16} className="mr-2 animate-spin"/> Signing in...</> : <>Sign in <ArrowRight size={16} className="ml-2"/></>}
          </Button>

          {lockInfo && (
            <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-4" data-testid="login-lock-card">
              <div className="flex items-start gap-3">
                <div className="h-9 w-9 rounded-md bg-red-100 text-red-700 grid place-items-center flex-shrink-0"><Lock size={16}/></div>
                <div className="min-w-0">
                  <div className="font-semibold text-red-900 text-sm">Account access suspended</div>
                  <p className="mt-1 text-sm text-red-900/80 leading-snug">{lockInfo.message}</p>
                  <a href={`mailto:${lockInfo.contactEmail}?subject=Accu%20Desk%20access%20restoration%20request`} className="mt-2 inline-flex text-sm text-red-900 underline hover:text-red-700" data-testid="login-lock-contact-link">
                    Contact {lockInfo.contactEmail}
                  </a>
                </div>
              </div>
            </div>
          )}

          <div className="mt-4 rounded-lg bg-emerald-50 border border-emerald-100 p-3 text-xs text-emerald-900">
            <strong>Tip:</strong> Create an account in a few seconds via <Link to="/signup" className="underline">Start free trial</Link>.
          </div>

          <p className="mt-8 text-sm text-stone-600 text-center">
            New to Accu Desk? <Link to="/signup" className="text-emerald-800 font-medium hover:text-emerald-900">Start your free trial</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
